import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Collapse, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { IconChartBar, IconChevronRight, IconLayoutSidebarLeftCollapse, IconList } from '@tabler/icons-react';
import useAppStore from 'src/stores/app';
import useUserStore from 'src/stores/user';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import LeadsmaniaCollapsedLogo from 'src/icons/LeadsmaniaCollapsedLogo';
import ChevroneUp from 'src/icons/ChevroneUp';
import LeadStreamsService from 'src/services/LeadStreamsService';
import AIManagersService from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import AcquisitionAnalyticsService from 'src/services/AcquisitionAnalyticsService';
import { history } from 'src/utils/history';
import { selectStreamsForMenu } from 'src/utils/leadStreams';
import LinkIcon from 'src/icons/Link';
import UserPlus from 'src/icons/UserPlus';
import NotificationIcon from 'src/icons/NotificationIcon';
import { ROUTES_URL } from 'src/constants/routes';
import FeedCardsIcon from 'src/icons/FeedCardsIcon';
import { AI_MANAGERS_AVAILABLE } from 'src/constants/features';
import useOnboardingLogoTarget from 'src/hooks/useOnboardingLogoTarget';

import ProfileDropdown from './components/ProfileDropdown';
import FlowNavigation from './components/FlowNavigation';
import AIManagersMenu from './components/AIManagersMenu';
import { AccordionButton } from './components/AccordionMenu';

import type { Stream } from 'src/services/LeadStreamsService/agent';
import type { AIManager } from 'src/services/AIManagersService';

type MenuProps = { open: boolean; onClose: () => void; anchore: 'right' | 'left'; variant: 'temporary' | 'permanent' };
export default function MenuComponent({ open, onClose, anchore, variant }: MenuProps) {
    const location = useLocation();
    const mobile = useIsMobileSize();
    const collapsed = useAppStore((s) => s.isSidebarCollapsed) && !mobile;
    const toggle = useAppStore((s) => s.toggleSidebarCollapsed);
    const logoTarget = useOnboardingLogoTarget();
    const { email, bonusPoints } = useUserStore((s) => ({ email: s.email, bonusPoints: s.bonusPoints }));
    const [streams, setStreams] = useState<Stream[]>([]);
    const [managers, setManagers] = useState<AIManager[]>([]);
    const [streamsOpen, setStreamsOpen] = useState(true);
    const [managersOpen, setManagersOpen] = useState(true);
    const [streamsError, setStreamsError] = useState(false);
    const [managersError, setManagersError] = useState(false);
    const [showAcquisitionAnalytics, setShowAcquisitionAnalytics] = useState(false);
    useEffect(() => {
        let live = true;
        setShowAcquisitionAnalytics(false);
        void AcquisitionAnalyticsService.getCapabilities()
            .then(({ journeys }) => {
                if (live) setShowAcquisitionAnalytics(journeys);
            })
            .catch(() => {
                if (live) setShowAcquisitionAnalytics(false);
            });
        return () => {
            live = false;
        };
    }, [email]);
    useEffect(() => {
        let live = true;
        const loadStreams = () => {
            void LeadStreamsService.getStreams()
                .then((result) => {
                    if (live) {
                        setStreams(selectStreamsForMenu(result));
                        setStreamsError(false);
                    }
                })
                .catch(() => {
                    if (live) setStreamsError(true);
                });
        };
        const loadManagers = () => {
            if (!AI_MANAGERS_AVAILABLE) return;
            void AIManagersService.getManagers()
                .then((result) => {
                    if (live) {
                        setManagers(result);
                        setManagersError(false);
                    }
                })
                .catch(() => {
                    if (live) setManagersError(true);
                });
        };
        const loadTools = () => {
            loadStreams();
            loadManagers();
        };
        setStreams([]);
        setManagers([]);
        loadTools();
        const streamEvents = EventService.subscribe('LEAD_STREAMS_UPDATED', loadStreams);
        const managerEvents = EventService.subscribe('AI_MANAGERS_UPDATED', loadManagers);
        const menuEvents = EventService.subscribe('OPEN_MENU', loadTools);
        return () => {
            live = false;
            streamEvents.unsubscribe();
            managerEvents.unsubscribe();
            menuEvents.unsubscribe();
        };
    }, [email]);
    const go = (path: string) => {
        history.push(path);
        onClose();
        EventService.dispatch('CLOSE_MENU');
    };
    const item = (
        label: string,
        icon: React.ReactNode,
        path: string,
        selected = false,
        compact = false,
        soon = false
    ) => (
        <Tooltip
            title={collapsed ? `${label}${soon ? ' · Скоро' : ''}` : ''}
            placement="right"
            key={label}
        >
            <Button
                onClick={soon ? undefined : () => go(path)}
                aria-disabled={soon || undefined}
                aria-label={`${label}${soon ? ' · Скоро' : ''}`}
                aria-current={selected ? 'page' : undefined}
                sx={{
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    width: '100%',
                    minWidth: 0,
                    color: selected ? 'var(--lm-accent-ink)' : 'var(--lm-text)',
                    '--lm-menu-icon-primary': selected ? 'var(--lm-accent-ink)' : undefined,
                    '--lm-menu-icon-secondary': selected ? 'var(--lm-accent-contrast)' : undefined,
                    fontSize: compact ? 14 : 16,
                    fontWeight: 400,
                    gap: 1.5,
                    textTransform: 'none',
                    minHeight: collapsed ? 48 : compact ? 38 : 54,
                    height: 'auto',
                    borderRadius: compact ? '10px' : '16px',
                    border: compact ? '1px solid transparent' : '1px solid var(--lm-border)',
                    px: collapsed ? 0 : 2,
                    mb: compact ? 0 : 0.75,
                    bgcolor: selected ? 'var(--lm-canvas)' : 'var(--lm-surface)',
                    '&:hover': { bgcolor: 'var(--lm-canvas)' },
                    '&:focus-visible': { outline: '2px solid var(--lm-accent-ink)', outlineOffset: -2 },
                    ...(soon && {
                        color: 'var(--lm-muted)',
                        cursor: 'default',
                        '&:hover': { bgcolor: 'var(--lm-surface)' },
                    }),
                    '& svg': { width: 19, height: 19, flexShrink: 0 },
                }}
            >
                {icon}
                {!collapsed && (
                    <Box
                        component="span"
                        sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'left',
                        }}
                    >
                        {label}
                    </Box>
                )}
                {!collapsed && soon && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 11,
                            px: 0.8,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: 'var(--lm-canvas)',
                            color: 'var(--lm-muted)',
                        }}
                    >
                        Скоро
                    </Typography>
                )}
                {!collapsed && !compact && !soon && (
                    <IconChevronRight style={{ color: 'var(--lm-disabled)', width: 16 }} />
                )}
            </Button>
        </Tooltip>
    );
    const section = (title: string, expanded: boolean, toggleSection: () => void, content: React.ReactNode) => (
        <Box
            component="section"
            aria-label={title}
            sx={{ mb: 1.5 }}
        >
            <Button
                onClick={toggleSection}
                aria-expanded={expanded}
                aria-controls={title === 'Потоки лидов' ? 'menu-streams' : 'menu-managers'}
                sx={{ width: '100%', height: 42, justifyContent: 'space-between', px: 1, color: 'var(--lm-text)' }}
            >
                <Typography
                    component="span"
                    fontWeight={700}
                    fontSize={16}
                >
                    {title}
                </Typography>
                <AccordionButton isOpen={expanded} />
            </Button>
            <Collapse
                in={expanded}
                id={title === 'Потоки лидов' ? 'menu-streams' : 'menu-managers'}
            >
                {content}
            </Collapse>
        </Box>
    );
    return (
        <Drawer
            anchor={anchore}
            open={open}
            onClose={onClose}
            variant={variant}
            PaperProps={{
                sx: {
                    position: variant === 'permanent' ? 'sticky' : 'fixed',
                    top: 0,
                    width: mobile ? 'min(340px, 100vw)' : '100%',
                    height: '100dvh',
                    bgcolor: 'var(--lm-surface)',
                    border: 0,
                    boxShadow: 'none',
                    overflow: collapsed ? 'visible' : 'hidden',
                },
            }}
        >
            <Box
                component="nav"
                aria-label="Основное меню"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    px: 1,
                    '& .sidebar-expanded-toggle, & .sidebar-collapsed-toggle': {
                        opacity: 1,
                        pointerEvents: 'auto',
                        transition: 'opacity 0.16s ease, transform 0.16s ease, background-color 0.16s ease',
                    },
                    '& .sidebar-collapsed-toggle': { transform: 'translateX(-4px)' },
                    '&:hover .sidebar-expanded-toggle, &:focus-within .sidebar-expanded-toggle, &:hover .sidebar-collapsed-toggle, &:focus-within .sidebar-collapsed-toggle':
                        {
                            opacity: 1,
                            pointerEvents: 'auto',
                            transform: 'translateX(0)',
                        },
                    '@media (hover: none)': {
                        '& .sidebar-expanded-toggle, & .sidebar-collapsed-toggle': {
                            opacity: 1,
                            pointerEvents: 'auto',
                            transform: 'none',
                        },
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        position: 'relative',
                        minHeight: collapsed ? 76 : 80,
                        width: collapsed ? 48 : undefined,
                        mx: collapsed ? 'auto' : 0,
                        flexShrink: 0,
                        px: collapsed ? 0 : 1,
                    }}
                >
                    <Button
                        onClick={() => go(logoTarget)}
                        aria-label="Лидсмания"
                        disableRipple
                        sx={{
                            color: 'var(--lm-text)',
                            minWidth: 0,
                            p: 0.5,
                            gap: 1,
                            flexShrink: 0,
                            '& svg, & .MuiTypography-root': { transition: 'color .2s ease' },
                            '&:hover, &:focus-visible': {
                                bgcolor: 'transparent !important',
                                color: 'var(--lm-accent-ink)',
                                '& svg': { color: 'var(--lm-accent-ink)' },
                                '& .MuiTypography-root': { color: 'var(--lm-accent-ink)' },
                            },
                        }}
                    >
                        {
                            <LeadsmaniaCollapsedLogo
                                width={32}
                                height={32}
                            />
                        }
                        {!collapsed && (
                            <Typography
                                sx={{
                                    fontFamily: '"Inter", system-ui',
                                    fontSize: { xs: 20, lg: 22 },
                                    fontWeight: 850,
                                    lineHeight: '36px',
                                }}
                            >
                                Лидсмания
                            </Typography>
                        )}
                    </Button>
                    <Tooltip title={mobile ? '' : collapsed ? 'Развернуть меню' : 'Свернуть меню'}>
                        <IconButton
                            aria-label={mobile ? 'Закрыть меню' : collapsed ? 'Развернуть меню' : 'Свернуть меню'}
                            aria-expanded={mobile ? undefined : !collapsed}
                            className={
                                mobile ? undefined : collapsed ? 'sidebar-collapsed-toggle' : 'sidebar-expanded-toggle'
                            }
                            onClick={mobile ? onClose : toggle}
                            size="small"
                            sx={
                                mobile
                                    ? undefined
                                    : {
                                          position: collapsed ? 'absolute' : 'relative',
                                          left: collapsed ? 38 : undefined,
                                          top: collapsed ? 24 : undefined,
                                          zIndex: 1,
                                          flexShrink: 0,
                                          width: 20,
                                          height: 28,
                                          padding: 0,
                                          borderRadius: '10px',
                                          backgroundColor: 'var(--lm-border)',
                                          border: '1px solid var(--lm-border)',
                                          boxShadow: '0 2px 6px rgba(31, 31, 31, 0.06)',
                                          '&:hover': { backgroundColor: 'var(--lm-hover)' },
                                          '&:focus-visible': {
                                              outline: '2px solid var(--lm-accent-ink)',
                                              outlineOffset: 2,
                                          },
                                          '& svg': {
                                              height: 28,
                                              width: 24,
                                              transform: collapsed ? 'rotate(90deg)' : 'rotate(-90deg)',
                                          },
                                          '& svg path': {
                                              stroke: 'var(--lm-muted)',
                                              strokeWidth: 1.35,
                                              transition: 'stroke 0.16s ease',
                                          },
                                          '&:hover svg path': { stroke: 'var(--lm-text)' },
                                      }
                            }
                        >
                            {mobile ? <IconLayoutSidebarLeftCollapse size={20} /> : <ChevroneUp />}
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'thin',
                        pb: 1,
                    }}
                >
                    {item('Лиды', <FeedCardsIcon />, '/feed', location.pathname === '/feed')}

                    {collapsed ? (
                        <>
                            {item(
                                'Потоки лидов',
                                <IconList />,
                                '/leadstream',
                                location.pathname.startsWith('/leadstream')
                            )}
                            {item(
                                AI_MANAGERS_AVAILABLE ? 'AI-менеджеры' : 'ИИ-менеджеры',
                                <LeadsmaniaCollapsedLogo />,
                                '/ai-managers/new',
                                AI_MANAGERS_AVAILABLE && location.pathname.startsWith('/ai-managers'),
                                false,
                                !AI_MANAGERS_AVAILABLE
                            )}
                        </>
                    ) : (
                        <>
                            {section(
                                'Потоки лидов',
                                streamsOpen,
                                () => setStreamsOpen(!streamsOpen),
                                <>
                                    {streamsError && (
                                        <Typography
                                            color="secondary"
                                            variant="body2"
                                            sx={{ p: 1 }}
                                        >
                                            Не удалось обновить потоки
                                        </Typography>
                                    )}
                                    <FlowNavigation
                                        streams={streams}
                                        go={go}
                                    />
                                </>
                            )}
                            {AI_MANAGERS_AVAILABLE
                                ? section(
                                      'AI-менеджеры',
                                      managersOpen,
                                      () => setManagersOpen(!managersOpen),
                                      <>
                                          {managersError && (
                                              <Typography
                                                  color="secondary"
                                                  variant="body2"
                                                  sx={{ p: 1 }}
                                              >
                                                  Не удалось обновить менеджеров
                                              </Typography>
                                          )}
                                          <AIManagersMenu managers={managers} />
                                      </>
                                  )
                                : item('ИИ-менеджеры', <LeadsmaniaCollapsedLogo />, '', false, false, true)}
                        </>
                    )}
                    {item('Интеграции', <LinkIcon />, '/integrations', location.pathname === '/integrations')}
                    {item('Партнёрство', <UserPlus />, '/partnership', location.pathname === '/partnership')}
                    {item('Уведомления', <NotificationIcon />, ROUTES_URL.NOTIFICATIONS, location.pathname === ROUTES_URL.NOTIFICATIONS)}
                    {showAcquisitionAnalytics &&
                        item(
                            'Привлечение',
                            <IconChartBar />,
                            ROUTES_URL.ACQUISITION_ANALYTICS,
                            location.pathname === ROUTES_URL.ACQUISITION_ANALYTICS
                        )}
                </Box>
                <Box sx={{ flexShrink: 0, pt: 1, pb: 1, bgcolor: 'var(--lm-surface)' }}>
                    <ProfileDropdown
                        email={email}
                        bonusPoints={bonusPoints}
                        collapsed={collapsed}
                    />
                </Box>
            </Box>
        </Drawer>
    );
}
